/**
 * Decodes HEIC/HEIF images to PNG ArrayBuffer.
 * Uses native createImageBitmap (Chrome/Safari) with libheif-js fallback (Firefox).
 */

const HEIC_EXTENSIONS = new Set(["heic", "heif"]);

export function isHeicFile(filename: string): boolean {
	const ext = filename.split(".").pop()?.toLowerCase() || "";
	return HEIC_EXTENSIONS.has(ext);
}

async function decodeWithNative(buffer: ArrayBuffer): Promise<ArrayBuffer> {
	const blob = new Blob([buffer], { type: "image/heic" });
	const bitmap = await createImageBitmap(blob);
	const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
	const ctx = canvas.getContext("2d")!;
	ctx.drawImage(bitmap, 0, 0);
	bitmap.close();
	const outputBlob = await canvas.convertToBlob({ type: "image/png" });
	return outputBlob.arrayBuffer();
}

async function decodeWithLibheif(buffer: ArrayBuffer): Promise<ArrayBuffer> {
	// Use wasm-bundle build (no 'fs' dependency, browser-compatible)
	// @ts-ignore — no type declarations for wasm-bundle entry
	const libheif = await import("libheif-js/wasm-bundle");
	const decoder = new libheif.HeifDecoder();
	const data = decoder.decode(new Uint8Array(buffer));

	if (!data || data.length === 0) {
		throw new Error("libheif: no images found in HEIC file");
	}

	// Use the first (primary) image
	const image = data[0];
	const width = image.get_width();
	const height = image.get_height();

	// Get RGBA pixel data
	const canvas = new OffscreenCanvas(width, height);
	const ctx = canvas.getContext("2d")!;
	const imageData = ctx.createImageData(width, height);

	await new Promise<void>((resolve, reject) => {
		image.display(imageData, (displayData: ImageData | null) => {
			if (!displayData) {
				reject(new Error("libheif: failed to decode image data"));
				return;
			}
			ctx.putImageData(displayData, 0, 0);
			resolve();
		});
	});

	const outputBlob = await canvas.convertToBlob({ type: "image/png" });
	return outputBlob.arrayBuffer();
}

/**
 * Decode a HEIC/HEIF file to PNG ArrayBuffer.
 * Tries native browser decoding first, falls back to libheif-js WASM decoder.
 */
export async function decodeHeicToPng(buffer: ArrayBuffer): Promise<ArrayBuffer> {
	// Try native first (Chrome 85+, Safari 17+)
	try {
		return await decodeWithNative(buffer);
	} catch {
		// Native not supported (Firefox, older browsers)
	}

	// Fall back to libheif-js WASM decoder
	return decodeWithLibheif(buffer);
}
